import { ProtectedLayoutProvider } from '@mobile-mapping/providers';
import { GainsightProvider } from '@trimble/analytics';
import { Route, Router } from '@solidjs/router';
import { QueryClient, QueryClientProvider } from '@tanstack/solid-query';
import { Notifications } from '@trimble/solidjs-notifications';
import { useLogger } from '@trimble/solidjs-utils';
import { defineCustomElements as defineModusWebComponents } from '@trimble-oss/moduswebcomponents/loader';
import { t } from '@trimble/i18n';
import {
  createTIDAuthClientFromConfig,
  TIDProvider,
} from '@trimble/trimble-id-solidjs';
import {
  createStorageAdapterFromHelper,
  TrimbleIdentityService,
} from '@trimble/trimble-identity';
import 'solid-devtools';
import { Component, createSignal, lazy, onMount } from 'solid-js';
import { render } from 'solid-js/web';
import { makePersisted } from '@solid-primitives/storage';
import { DeveloperRouteGuard } from './app/components/DeveloperRouteGuard';
import HomePage from './app/pages/HomePage';
import PopupPage from './app/pages/PopupPage';
import { loadConfig } from './config/config';
import { initializeGainsight } from './utils/gainsight';

const DebugPage = lazy(() => import('./app/pages/DebugPage/DebugPage'));
const FeatureDeploymentsPage = lazy(
  () => import('./app/pages/FeatureDeploymentsPage/FeatureDeploymentsPage')
);

const ProtectedDebugPage: Component = () => (
  <DeveloperRouteGuard>
    <DebugPage />
  </DeveloperRouteGuard>
);

const ProtectedFeatureDeploymentsPage: Component = () => (
  <DeveloperRouteGuard>
    <FeatureDeploymentsPage />
  </DeveloperRouteGuard>
);

import './styles.css';
import './tc.styles.css';
import './override.css';

import '@trimble-oss/moduswebcomponents/modus-wc-styles.css';
import { defineUvCameraControlSelector } from '@trimble/web3d-web-components';
import { LoggerProvider } from '@trimble/solidjs-utils';

defineModusWebComponents();
defineUvCameraControlSelector();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Disable refetch, otherwise 3D view will randomly zoom out.
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: Infinity,
    },
  },
});

const root = document.getElementById('root');

if (import.meta.env.DEV && !(root instanceof HTMLElement)) {
  throw new Error(
    'Root element not found. Did you forget to add it to your index.html? Or maybe the id attribute got misspelled?'
  );
}

const logger = useLogger().getLogger();
logger.debug('Application starting...');

async function bootstrap(): Promise<void> {
  const config = await loadConfig();
  initializeGainsight(config.gainsightKey);

  const identityService = new TrimbleIdentityService(
    createStorageAdapterFromHelper(true)
  );
  const tidClient = await createTIDAuthClientFromConfig(identityService, {
    configurationEndpoint: config.tidSettings.configurationEndpoint,
    clientId: config.tidSettings.clientId,
    scopes: config.tidSettings.scopes,
    redirectUrl: window.location.origin,
    logoutRedirectUrl: `${window.location.origin}/logout`,
    connectDomain: config.tcTcpsDomain,
  });

  render(() => {
    const [returnUrl, setReturnUrl] = makePersisted(
      // eslint-disable-next-line solid/reactivity
      createSignal<string>(),
      {
        name: 'auth-return-url',
        storage: sessionStorage,
      }
    );

    const handleRedirect = (): void => {
      window.location.href = returnUrl() ?? window.location.origin;
    };

    onMount(() => {
      document.title = t('mobileMapping.projectNumarkDocumentTitle');

      // If not an auth flow request, persist the URL so we can redirect back after authentication.
      if (!new URLSearchParams(window.location.search).has('code')) {
        setReturnUrl(window.location.href);
      }
    });

    return (
      <LoggerProvider loggerConfigs={[...config.loggers]}>
        <TIDProvider tidClient={tidClient} onRedirectCallback={handleRedirect}>
          <GainsightProvider>
            <QueryClientProvider client={queryClient}>
              <ProtectedLayoutProvider
                alignmentsDomain={config.alignmentsDomain}
                mobileMappingDomain={config.mobileMappingDomain}
                seuratDomain={config.seuratDomain}
                imagingDomain={config.imagingDomain}
                tcTcpsDomain={config.tcTcpsDomain}
                tcUserDomain={config.tcUserDomain}
                ecomDomain={config.ecomDomain}
                coordinatesDomain={config.coordinatesDomain}
                clientName={config.tidSettings.appName}
                clientVersion={config.tidSettings.version}
                isProductionEnvironment={config.production}
                envName={config.envName}
                deployedTag={config.deployedTag}
                connectUrl={config.connectUrl}
                mapTileServiceBaseUrl={config.mapTileServiceBaseUrl}
                mapTileLayerQueryParams={config.mapTileLayerQueryParams}
                mapTileAuthToken={config.mapTileAuthToken}
                fxWorkspaceDomain={config.fxWorkspaceDomain}
                fxMapsServiceBaseUrl={config.fxMapsServiceBaseUrl}
                fxFeaturesServiceBaseUrl={config.fxFeaturesServiceBaseUrl}
                fxTilesServiceBaseUrl={config.fxTilesServiceBaseUrl}
              >
                <Router base={import.meta.env['VITE_BASE_PATH']}>
                  <Route path="/" component={HomePage} />
                  <Route path="/debug" component={ProtectedDebugPage} />
                  <Route
                    path="/deployments"
                    component={ProtectedFeatureDeploymentsPage}
                  />
                  <Route path="/popup" component={PopupPage} />
                </Router>
              </ProtectedLayoutProvider>
            </QueryClientProvider>
          </GainsightProvider>
          <Notifications />
        </TIDProvider>
      </LoggerProvider>
    );
  }, root!);
}

bootstrap().catch((error: unknown) => {
  logger.error('Failed to bootstrap application:', error as object);
  if (root) {
    root.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: sans-serif;">
        <div style="text-align: center;">
          <h1>Application Error</h1>
          <p>Failed to load application configuration. Please try refreshing the page.</p>
        </div>
      </div>
    `;
  }
});
